import {
  useWSModalCustom,
  useWSSnackbar,
  WSAlert,
  WSButton,
  WSControl,
  WSDivider,
  WSDropFileInput,
  WSFileInput,
  WSFlexBox,
  WSForm,
  WSIcon,
  WSInputDate,
  WSInputNumber,
  WSInputText,
  WSInputTextarea,
  WSList,
  WSLoader,
  WSModal,
  WSModalCustomProps,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import { Transaction, TransactionType } from "@wingspanhq/wingspan-api-sdk";
import React, { ChangeEvent } from "react";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import { queriesTaxWithholdings, queriesTransactions } from "@query";
import { ErrorAlert } from "@components/ErrorAlert";
import { DataItem } from "../../../shared/components/DataItem";
import { PrivateURL } from "../../../components/PrivateFileURL/PrivateFileURL";
import { useMutationMemberPrivateFileCreate } from "../../../query/files/mutations";
import { INVOICING_PATH } from "../../Invoicing";
import { routeHelpersPayablesDetails } from "../../Payables/paths";
import { PATH_TAX_WITHHOLDINGS } from "../../TaxWithholdings/paths";
import {
  CategoryPickerField,
  CategoryPickerValue
} from "../components/CategoryPicker";
import { RefundExplainer } from "../components/RefundExplainer";
import { TaxSavings } from "../components/TaxSavings";
import { TRANSACTION_TYPE_SELECT_OPTIONS } from "../utils/transactionTypeOptions";

type FormData = {
  amount: number | null;
  type: TransactionType;
  date: Date;
  name: string;
  isBusiness: boolean;
  businessAmount: number | null;
  category: CategoryPickerValue;
  note: string;
};

const ReceiptField: React.FC<{ transaction: Transaction }> = props => {
  const { openSnackbar } = useWSSnackbar();
  const [uploadFile, uploadMeta] = useMutationMemberPrivateFileCreate({
    hidden: true
  });
  const [updateTransaction, updateMeta] =
    queriesTransactions.updateTransaction.useMutation();

  const loading = uploadMeta.isLoading || updateMeta.isLoading;

  const uploadReceipt = async (file?: File) => {
    if (!file) {
      return;
    }
    const data = new FormData();
    data.append("file", file);
    await uploadFile(data, {
      onSuccess: async response => {
        if (response.length > 0) {
          await updateTransaction(
            {
              transactionId: props.transaction.id,
              transactionUpdate: { receiptFileId: response[0].fileId }
            },
            {
              onSuccess: () =>
                openSnackbar({
                  icon: { name: "check" },
                  message: "Receipt attached"
                })
            }
          );
        }
      }
    });
  };

  const removeReceipt = async () => {
    await updateTransaction(
      {
        transactionId: props.transaction.id,
        transactionUpdate: { receiptFileId: "" }
      },
      {
        onSuccess: () =>
          openSnackbar({ icon: { name: "check" }, message: "Receipt removed" })
      }
    );
  };

  return (
    <WSList gap="S">
      <WSText.ParagraphSm color="gray500">Receipt</WSText.ParagraphSm>
      {props.transaction.receiptFileId ? (
        <PrivateURL fileId={props.transaction.receiptFileId}>
          {(status, url) =>
            status === "loading" ? (
              <WSLoader.Spinner />
            ) : (
              <WSFlexBox.CenterY wrap="nowrap">
                {status === "success" ? (
                  <WSButton.Link
                    type="button"
                    mr="L"
                    onClick={() => window.open(url, "_blank")}
                  >
                    View receipt
                  </WSButton.Link>
                ) : null}
                <WSFileInput
                  mr="L"
                  name="replaceReceipt"
                  accept="image/*,.pdf"
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    void uploadReceipt(event.target.files?.[0])
                  }
                >
                  <WSButton.Link type="button" loading={loading}>
                    Replace
                  </WSButton.Link>
                </WSFileInput>
                <WSButton.Link
                  type="button"
                  loading={loading}
                  onClick={() => void removeReceipt()}
                >
                  Remove
                </WSButton.Link>
              </WSFlexBox.CenterY>
            )
          }
        </PrivateURL>
      ) : (
        <WSDropFileInput
          name="uploadReceipt"
          buttonText="Upload receipt"
          buttonProps={{ icon: "upload", type: "button" }}
          loading={loading}
          accept="image/*,.pdf"
          onDropAccepted={accepted => void uploadReceipt(accepted[0])}
        />
      )}
    </WSList>
  );
};

type Props = { transaction: Transaction } & WSModalCustomProps<void>;

const ModalTransactionDetails: React.FC<Props> = props => {
  const { openSnackbar } = useWSSnackbar();
  const history = useHistory();

  const queryTransaction = queriesTransactions.getTransaction.useQuery({
    transactionId: props.transaction.id
  });
  const transaction = queryTransaction.data ?? props.transaction;

  const [updateTransaction, updateMeta] =
    queriesTransactions.updateTransaction.useMutation();
  const [deleteTransaction, deleteMeta] =
    queriesTransactions.deleteTransaction.useMutation();
  const queryTaxWithholdingSettings =
    queriesTaxWithholdings.getTaxWithholdingSettings.useQuery();

  const isPayable = transaction.type !== TransactionType.INCOME;
  const goToLinkedDoc = () => {
    if (!transaction.invoiceId) {
      return;
    }
    props.onClose();
    if (isPayable) {
      history.push(
        routeHelpersPayablesDetails.createLocationObject({
          tab: "all",
          payableId: transaction.invoiceId
        })
      );
    } else {
      history.push(`${INVOICING_PATH}/${transaction.invoiceId}`);
    }
  };

  const handleSubmit = async (formData: FormData) => {
    await updateTransaction(
      {
        transactionId: transaction.id,
        transactionUpdate: {
          amount: Number(formData.amount ?? 0),
          type: formData.type,
          date: formData.date,
          name: formData.name,
          isBusiness: formData.isBusiness,
          businessAmount: formData.isBusiness
            ? Number(formData.businessAmount ?? 0)
            : undefined,
          wsCategory: formData.category.wsCategory || undefined,
          subcategory: formData.category.subcategory || null,
          note: formData.note || undefined
        }
      },
      {
        onSuccess: () => {
          openSnackbar({
            icon: { name: "check" },
            message: "Transaction updated"
          });
          props.onClose();
        }
      }
    );
  };

  const handleDelete = async () => {
    await deleteTransaction(
      { transactionId: transaction.id },
      {
        onSuccess: () => {
          openSnackbar({
            icon: { name: "check" },
            message: "Transaction deleted"
          });
          props.onClose();
        }
      }
    );
  };

  return (
    <WSForm<FormData>
      onSubmit={handleSubmit}
      defaultValues={{
        amount: Number(props.transaction.amount ?? 0),
        type: props.transaction.type,
        date: props.transaction.date
          ? new Date(props.transaction.date)
          : new Date(),
        name: props.transaction.name,
        isBusiness: props.transaction.isBusiness,
        businessAmount: props.transaction.businessAmount
          ? Number(props.transaction.businessAmount)
          : null,
        category: {
          wsCategory: props.transaction.wsCategory,
          subcategory: props.transaction.subcategory
        },
        note: props.transaction.note ?? ""
      }}
      validationSchema={Yup.object().shape({
        amount: Yup.number()
          .nullable()
          .required("Required")
          .moreThan(0, "Enter an amount"),
        type: Yup.string().required("Required"),
        date: Yup.date().required("Required"),
        name: Yup.string().required("Required").max(100, "Too long")
      })}
    >
      <WSModal
        title={transaction.merchantName || transaction.name}
        size="M"
        onClose={() => props.onClose()}
        actions={{
          buttons: [
            {
              label: "Delete",
              kind: "Secondary",
              destructive: true,
              type: "button",
              loading: deleteMeta.isLoading,
              onClick: () => {
                void handleDelete();
              }
            },
            { label: "Save changes", loading: updateMeta.isLoading }
          ]
        }}
      >
        <WSList gap="XL">
          {transaction.scheduleCLineNumber ? (
            <DataItem
              label="Schedule C"
              value={`Line ${transaction.scheduleCLineNumber}${
                transaction.scheduleCDescription
                  ? ` · ${transaction.scheduleCDescription}`
                  : ""
              }`}
            />
          ) : null}

          <TaxSavings transaction={transaction} />

          {transaction.type === TransactionType.REFUND ? (
            <RefundExplainer />
          ) : null}

          {transaction.invoiceId ? (
            <WSFlexBox.CenterY justify="space-between" wrap="nowrap">
              <WSFlexBox.CenterY wrap="nowrap">
                <WSIcon block name="link" mr="M" color="gray500" />
                <WSText.ParagraphSm color="gray500">
                  {isPayable ? "Linked payable" : "Linked invoice"}
                </WSText.ParagraphSm>
              </WSFlexBox.CenterY>
              <WSButton.Link
                type="button"
                rightIcon="chevron-right"
                onClick={goToLinkedDoc}
              >
                View {isPayable ? "payable" : "invoice"}
              </WSButton.Link>
            </WSFlexBox.CenterY>
          ) : null}

          {transaction.invoiceId &&
          queryTaxWithholdingSettings.data?.isEnabled !== true ? (
            <WSAlert
              theme="warning"
              icon="alert-circle"
              title="Tax withholdings is not enabled"
              action={{
                text: "Learn more",
                onClick: () => history.push(PATH_TAX_WITHHOLDINGS)
              }}
            >
              Enable tax withholdings today to ensure you're setting aside
              enough for {new Date().getFullYear()} taxes.
            </WSAlert>
          ) : null}

          <ReceiptField transaction={transaction} />

          <WSDivider />

          <WSList gap="L">
            <WSForm.Field
              name="amount"
              label="Amount"
              component={WSInputNumber}
              componentProps={{ min: 0, maxFractionDigits: 2 }}
            />
            <WSForm.Field
              name="type"
              label="Type"
              component={WSSelect}
              componentProps={{ options: TRANSACTION_TYPE_SELECT_OPTIONS }}
            />
            <WSForm.Field name="date" label="Date" component={WSInputDate} />
            <WSForm.Field
              name="name"
              label="Description"
              component={WSInputText}
            />
            <WSForm.Field
              name="isBusiness"
              component={WSControl}
              componentProps={{ type: "switch", label: "Business" }}
            />
            <WSForm.Value name="isBusiness">
              {isBusiness =>
                isBusiness ? (
                  <WSForm.Field
                    name="businessAmount"
                    label="Business amount"
                    component={WSInputNumber}
                    componentProps={{ min: 0, maxFractionDigits: 2 }}
                  />
                ) : null
              }
            </WSForm.Value>
            <WSForm.Field
              name="category"
              label="Category"
              component={CategoryPickerField}
            />
            <WSForm.Field
              name="note"
              label="Note"
              component={WSInputTextarea}
            />
            <ErrorAlert
              context="Bookkeeping"
              error={updateMeta.error || deleteMeta.error}
            />
          </WSList>
        </WSList>
      </WSModal>
    </WSForm>
  );
};

export const useModalTransactionDetails = () =>
  useWSModalCustom(ModalTransactionDetails);
